import type { DataRouter } from 'react-router-dom';
import { routeOf, type Route } from '@/lib/route';

let router: DataRouter | null = null;
let pending: string | null = null;
let replacing = false;

export function installNavigator(r: DataRouter): void {
  router = r;
  r.subscribe((s) => { if (s.navigation.state === 'idle') pending = null; });
}

const hereOf = (r: DataRouter) => r.state.location.pathname + r.state.location.search + r.state.location.hash;

/** Navigate from outside React. The same destination asked twice in one tick
 *  (a tab store and then the section move) is one history entry, not two. */
export function go(to: string, opts: { replace?: boolean } = {}): void {
  if (!router || to === hereOf(router) || to === pending) return;
  pending = to;
  void router.navigate(to, { replace: opts.replace ?? replacing });
}

/** Every `go` inside `fn` replaces the current entry — for arrivals from a link,
 *  which must not leave the link itself behind Back. */
export function replaceWithin<T>(fn: () => T): T {
  replacing = true;
  try { return fn(); } finally { replacing = false; }
}

/** Spend one-shot query parameters: the same address without them. */
export function dropParams(...keys: string[]): void {
  if (!router) return;
  const { pathname, search, hash } = router.state.location;
  const params = new URLSearchParams(search);
  if (!keys.some((k) => params.has(k))) return;
  keys.forEach((k) => params.delete(k));
  const rest = params.toString();
  go(pathname + (rest ? `?${rest}` : '') + hash, { replace: true });
}

export function currentRoute(): Route | null {
  return router ? routeOf(router.state.matches.map((m) => ({ handle: m.route.handle, params: m.params }))) : null;
}
